import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { SupportButton } from "components/snackbars/SupportButton";

import {
  HubPlan,
  PlanInfo,
  Subscription as SubscriptionData,
} from "data/models";

import { ProductCard } from "./product-card";
import { getChangeType, getPlan } from "./change-summary";
import {
  getPlanCardFeatures,
  getPlanDescription,
} from "utils/subscription-plans";

interface AvailablePlansSectionProps {
  planInfo: PlanInfo;
  subscription: SubscriptionData;
  annualPlans: boolean;
  setAnnualPlans: (val: boolean) => void;
  selectedPrice: string | null;
  onSelect: (priceId: string, type: "selectable" | "trial") => void;
  onOpenFeatureComparison: () => void;
  onOpenFeatureCosts: () => void;
  canDoTrial: boolean;
  onTrial: boolean;
  currentPlan: HubPlan | null;
}

export const AvailablePlansSection = ({
  planInfo,
  subscription,
  annualPlans,
  setAnnualPlans,
  selectedPrice,
  onSelect,
  onOpenFeatureComparison,
  onOpenFeatureCosts,
  canDoTrial,
  onTrial,
  currentPlan,
}: AvailablePlansSectionProps) => {
  const cadence: "month" | "year" = annualPlans ? "year" : "month";
  const activePriceId = subscription.activeSubscription?.plan.price_lookup_id;

  const products = planInfo.products.filter((product) => {
    const price = getPlan(product.productKey, cadence, planInfo);
    return product.selfServe && !!price;
  });
  const contactProducts = planInfo.products.filter(
    (product) => !product.selfServe,
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold">Available Plans</h2>
        <ToggleGroup
          type="single"
          value={annualPlans ? "year" : "month"}
          onValueChange={(val) => {
            if (val) setAnnualPlans(val === "year");
          }}
          className="w-full md:ml-2 md:w-auto"
          variant="outline"
        >
          <ToggleGroupItem value="month" className="flex-1 md:flex-initial">
            Monthly
          </ToggleGroupItem>
          <ToggleGroupItem value="year" className="flex-1 md:flex-initial">
            Annual
          </ToggleGroupItem>
        </ToggleGroup>
        <div className="flex w-full flex-col gap-1 md:ml-auto md:w-auto md:flex-row">
          <Button
            variant="outline"
            className="w-full md:w-auto"
            onClick={onOpenFeatureComparison}
          >
            Feature Comparison
          </Button>
          <Button
            variant="outline"
            className="w-full md:w-auto"
            onClick={onOpenFeatureCosts}
          >
            Feature Costs
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {products.map((product) => {
          const price = getPlan(product.productKey, cadence, planInfo);
          const isCurrent =
            price?.price_lookup_id != null &&
            price?.price_lookup_id === activePriceId;

          let type: "current" | "trial" | "selectable";
          if (isCurrent) type = "current";
          else if (
            canDoTrial &&
            price?.trial_allowed &&
            getChangeType(price, currentPlan) !== "downgrade"
          )
            type = "trial";
          else type = "selectable";

          return (
            <ProductCard
              key={product.productKey}
              priceId={price?.price_lookup_id ?? null}
              title={product.displayName}
              description={getPlanDescription(product.productKey)}
              dollarCost={price?.dollar_cost ?? null}
              cadence={cadence}
              selected={selectedPrice === price?.price_lookup_id}
              disabled={
                price?.price_lookup_id === activePriceId &&
                !onTrial &&
                product.selfServe
              }
              type={type}
              isCurrentTrial={isCurrent && onTrial}
              features={getPlanCardFeatures(product)}
              handleSelect={(priceId) =>
                onSelect(priceId, type === "trial" ? "trial" : "selectable")
              }
            />
          );
        })}
      </div>
      {contactProducts.map((product) => (
        <div
          key={product.productKey}
          className="flex flex-col gap-4 rounded-xl border bg-muted/30 p-6 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">{product.displayName}</h3>
            <p className="text-sm text-muted-foreground">
              Need higher volumes or a custom plan? Talk to us about your
              requirements.
            </p>
          </div>
          <SupportButton
            text="Contact us"
            variant="outline"
            className="shrink-0"
          />
        </div>
      ))}
    </div>
  );
};
